import { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, MapPin, Calendar, Loader2, Truck, ClipboardList, Clock, CheckCircle2, BarChart3, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInCalendarDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePermissions } from '../contexts/PermissionsContext';
import { listsEqualById } from '../lib/listDiff';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUTS = {
    a_reserver: { label: 'A reserver', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    reserve: { label: 'Reserve', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    termine: { label: 'Termine', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

function formatDate(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy', { locale: fr }); } catch { return d; } }

export default function MonteMeublePage() {
    const { can } = usePermissions();
    const readOnly = !can('monte_meuble', 'write');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(null);

    const fetchData = (silent = false) => {
        if (!silent) setLoading(true);
        axios.get(`${API}/api/monte-meuble`, { withCredentials: true })
            .then(res => setItems(prev => listsEqualById(prev, res.data) ? prev : res.data))
            .catch(() => { if (!silent) toast.error('Erreur de chargement'); })
            .finally(() => { if (!silent) setLoading(false); });
    };
    useEffect(() => { fetchData(); }, []);

    useAutoRefresh(() => fetchData(true));

    const handleStatut = async (id, statut) => {
        try {
            await axios.patch(`${API}/api/monte-meuble/${id}/statut?statut=${statut}`, {}, { withCredentials: true });
            toast.success('Statut mis a jour');
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const isUrgent = (item) => {
        if (item.statut !== 'a_reserver' || !item.date_demenagement) return false;
        const jours = differenceInCalendarDays(new Date(item.date_demenagement), new Date());
        return jours >= 0 && jours <= 5;
    };
    const urgent = items.filter(isUrgent).length;

    const filtered = items.filter(c => {
        if (activeFilter === 'a_reserver' && c.statut !== 'a_reserver') return false;
        if (activeFilter === 'reserve' && c.statut !== 'reserve') return false;
        if (activeFilter === 'termine' && c.statut !== 'termine') return false;
        if (activeFilter === 'urgent' && !isUrgent(c)) return false;
        if (!search) return true;
        const s = search.toLowerCase();
        return (c.client_nom || '').toLowerCase().includes(s) || (c.adresse || '').toLowerCase().includes(s);
    }).sort((a, b) => (isUrgent(b) ? 1 : 0) - (isUrgent(a) ? 1 : 0));

    const total = items.length;
    const aReserver = items.filter(i => i.statut === 'a_reserver').length;
    const reserve = items.filter(i => i.statut === 'reserve').length;
    const termine = items.filter(i => i.statut === 'termine').length;

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="monte-meuble-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Monte-meuble</h1>
                    <p className="mt-1 text-slate-500">Demandes de monte-meuble</p>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="mm-stats">
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'all' ? null : 'all')} className={`text-left transition-all ${activeFilter === 'all' ? 'ring-2 ring-slate-400' : 'hover:shadow-md'}`} data-testid="mm-filter-total">
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                <BarChart3 size={20} className="text-slate-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-900" data-testid="mm-stat-total">{total}</p>
                                <p className="text-xs text-slate-500">Total prestations</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'a_reserver' ? null : 'a_reserver')} className={`text-left transition-all ${activeFilter === 'a_reserver' ? 'ring-2 ring-amber-400' : 'hover:shadow-md'}`} data-testid="mm-filter-a-reserver">
                    <Card className="bg-white rounded-xl border border-amber-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                                <ClipboardList size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-amber-700" data-testid="mm-stat-a-reserver">{aReserver}</p>
                                <p className="text-xs text-slate-500">A réserver</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'reserve' ? null : 'reserve')} className={`text-left transition-all ${activeFilter === 'reserve' ? 'ring-2 ring-blue-400' : 'hover:shadow-md'}`} data-testid="mm-filter-reserve">
                    <Card className="bg-white rounded-xl border border-blue-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                <Clock size={20} className="text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-blue-700" data-testid="mm-stat-reserve">{reserve}</p>
                                <p className="text-xs text-slate-500">Déjà réservé</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'termine' ? null : 'termine')} className={`text-left transition-all ${activeFilter === 'termine' ? 'ring-2 ring-emerald-400' : 'hover:shadow-md'}`} data-testid="mm-filter-termine">
                    <Card className="bg-white rounded-xl border border-emerald-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                                <CheckCircle2 size={20} className="text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-emerald-700" data-testid="mm-stat-termine">{termine}</p>
                                <p className="text-xs text-slate-500">Terminé</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'urgent' ? null : 'urgent')} className={`text-left transition-all ${activeFilter === 'urgent' ? 'ring-2 ring-red-400' : 'hover:shadow-md'}`} data-testid="mm-filter-urgent">
                    <Card className={`bg-white rounded-xl border shadow-sm h-full ${urgent > 0 ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${urgent > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                                <AlertTriangle size={20} className={urgent > 0 ? 'text-red-600' : 'text-slate-400'} />
                            </div>
                            <div>
                                <p className={`text-2xl font-bold ${urgent > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid="mm-stat-urgent">{urgent}</p>
                                <p className="text-xs text-slate-500">Urgent</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
            </div>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Rechercher (nom, adresse)..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="mm-search" />
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <Truck size={20} className="text-[#2e7cd6]" />
                        Liste des monte-meubles ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucun monte-meuble</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[600px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Date demenagement</TableHead>
                                        <TableHead>Adresse</TableHead>
                                        <TableHead>Statut</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(item => (
                                        <TableRow key={item.id} data-testid={`mm-row-${item.id}`} className={isUrgent(item) ? 'bg-red-50/50' : ''}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                                                        <Truck size={20} className="text-amber-600" />
                                                    </div>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <p className="font-medium text-slate-900 truncate">{item.client_nom}</p>
                                                        {isUrgent(item) && (
                                                            <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0 h-5 animate-pulse" data-testid={`mm-urgent-${item.id}`}>
                                                                URGENT
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar size={12} className="text-slate-400" />
                                                    <span className={`text-sm ${item.date_demenagement ? 'text-slate-700' : 'text-slate-400 italic'}`}>{item.date_demenagement ? formatDate(item.date_demenagement) : 'Non programmé'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-start gap-1.5">
                                                    <MapPin size={12} className={item.type === 'depart' ? 'text-emerald-500 mt-0.5' : 'text-red-500 mt-0.5'} />
                                                    <div>
                                                        <span className="text-sm text-slate-600 line-clamp-1">{item.adresse || '-'}</span>
                                                        <Badge variant="outline" className={`text-xs mt-0.5 ${item.type === 'depart' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                                            {item.type === 'depart' ? 'Depart' : 'Arrivee'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {item.statut === 'a_reserver' && !readOnly ? (
                                                    <Select value={item.statut} onValueChange={(v) => handleStatut(item.id, v)}>
                                                        <SelectTrigger className="w-[150px] h-8" data-testid={`mm-statut-${item.id}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="a_reserver">
                                                                <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUTS.a_reserver.color}`}>{STATUTS.a_reserver.label}</span>
                                                            </SelectItem>
                                                            <SelectItem value="reserve">
                                                                <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUTS.reserve.color}`}>{STATUTS.reserve.label}</span>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <Badge variant="outline" className={`text-xs border ${(STATUTS[item.statut] || STATUTS.a_reserver).color}`}>
                                                        {(STATUTS[item.statut] || STATUTS.a_reserver).label}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

